import { DEFAULT_BLUEPRINT_TEXT } from './constants.js';

const styleTypeMap = {
  fromTiptap: {
    bold: 'fontWeight',
    italic: 'fontStyle',
    underline: 'underline',
  },
  fromFabric: {
    fontWeight: 'bold',
    fontStyle: 'italic',
    underline: 'underline',
  },
};

/**
 * Transform tiptap content into a two-dimensional array represnting lines and text chunks
 * Note: This previously was recursive but now does not expect nested block tags from tiptap, depth = 1
 * @param {Object} tiptapContent The tip tap top-level 'content' entry
 */
export const getLinesFromTiptap = tiptapContent => {
  const lines = [];

  tiptapContent.forEach(({ content, type }) => {
    if (type.toLowerCase() === 'paragraph') {
      let line = [];

      if (content) {
        content.forEach(({ text, type: chunkType, marks }) => {
          if (chunkType === 'hardBreak') {
            lines.push(line); // Add current line and start a new line after a hardBreak
            line = [];
          } else {
            line.push({
              text: text || '',
              styles: marks?.map(({ type: markType }) => markType),
            });
          }
        });
      }

      // Ensure the last line is added, whether it's empty or not
      lines.push(line);
    } else if (type.toLowerCase() === 'hardBreak') {
      lines.push([]); // Treat each hardBreak as a new empty line
    }
  });

  return lines;
};

/** @todo: Document these conversion methods with example objects */
export const fromTiptap = tiptapState => {
  /** @todo: Handle errors */
  const lines = getLinesFromTiptap(tiptapState.content);
  const content = lines.reduce((acc, chunks, index) => {
    /* eslint-disable no-param-reassign */
    acc.text ||= '';

    const lineStyles = {};
    let lineText = '';

    chunks?.forEach(({ text, styles }) => {
      const filteredStyles =
        styles
          ?.map(
            type =>
              styleTypeMap.fromTiptap[type] && {
                prop: styleTypeMap.fromTiptap[type],
                value: type,
              },
          )
          .filter(Boolean) || [];

      let startIndex = lineText.length;
      lineText += text;

      if (filteredStyles?.length) {
        const translatedStyles = filteredStyles.reduce(
          (combinedStyles, { prop, value }) => {
            combinedStyles[prop] = prop === 'underline' ? true : value;
            return combinedStyles;
          },
          {},
        );

        // eslint-disable-next-line no-unused-vars
        for (const _ch of text) {
          lineStyles[startIndex] = translatedStyles;
          startIndex += 1;
        }
      }
    });

    acc.text += `${lineText}${index !== lines.length - 1 ? '\n' : ''}`;

    if (Object.keys(lineStyles).length) {
      acc.styles ||= {};
      acc.styles[index] = lineStyles;
    }

    return acc;
    /* eslint-disable no-param-reassign */
  }, {});

  return { text: content.text, styles: content.styles || {} };
};

/**
 * Compare relevant styles for equality
 * @param {Object.<{'fontWeight'|'fontStyle'|'underline'}, {string|boolean}>} a
 * @param {Object.<{'fontWeight'|'fontStyle'|'underline'}, {string|boolean}>} b
 */
const getStyleEquality = (a, b) =>
  a &&
  b &&
  a.fontWeight === b.fontWeight &&
  a.fontStyle === b.fontStyle &&
  a.underline === b.underline;

/**
 * Style options for text.
 * @typedef {Object} FabricStyleOption
 * @property {string} [fontWeight] - Weight of the font. 'bold' or a boolean.
 * @property {string} [fontStyle] - Style of the font. 'italic' or a boolean.
 * @property {(string|boolean)} [underline] - Underline style. true or a boolean.
 */

/**
 * Represents style configuration for each character index.
 * @typedef {Object.<string, Object.<string, FabricStyleOption>>} FabricStyles
 * @example
 *   {
 *     0: { 0: { fontWeight: 'bold' }, 1: { fontWeight: 'bold' }, 2: { underline: true }, 3: { underline: true }},
 *     1: { 0: { fontStyle: 'italic' }, 1: { fontStyle: 'italic' }, 2: { fontStyle: 'italic' }},
 *   }
 *   // Line 1 has two characters that are bold, followed by 2 that are underlined
 *   // Line 2 has three characters that are italicized
 */

/**
 * Convert fabricjs IText values, into a tiptap-compatible structure
 * @param {string} inputText - The text content
 * @param {FabricStyles} styles - The style array-like object from fabric where each key is a line that contains keys for each index in the inputText string with an associated style object
 */
export const toTiptap = (inputText, styles) => {
  let text = inputText;

  if (text === DEFAULT_BLUEPRINT_TEXT) text = '';
  const result = { type: 'doc', content: [] };
  const lines = text.split('\n');

  lines.forEach((line, lineIndex) => {
    // Tiptap doesn't allow empty text nodes
    if (!line?.trim()) {
      result.content.push({ type: 'paragraph' });
      return;
    }

    // grab the styles object for this line from fabric's object
    const lineStyles = styles[lineIndex];
    // the keys for this line, which represent character indexes in the string
    const characterStyles = Object.keys(lineStyles ?? {}).map(Number);

    // if there are no styles, or the text is unstyled, just push the text
    if (!lineStyles || !characterStyles.length) {
      result.content.push({
        type: 'paragraph',
        content: [{ type: 'text', text: line }],
      });
      return;
    }

    /** Get array made up of sequential subsets where styles are in a series to build chunks with for tiptap
     * @type Array.<number[]>
     */
    const styleGroups = [];

    /**
     * Keep a running list of the keys from fabric styles that have the same styles in sequential order
     * @type number[]
     */
    let series = [];
    characterStyles.forEach((ch, i) => {
      const charIndex = Number(ch);
      // If we're at the start of the array, any series must begin now
      if (i === 0) {
        series.push(charIndex);
        return;
      }

      const characterStyle = lineStyles[charIndex];
      const prevStyle = lineStyles[charIndex - 1];
      const stylesAreEqual = getStyleEquality(characterStyle, prevStyle);

      // Get the index of the character in the string, from the previous item in this array
      const previousCharIndex = characterStyles[i - 1];

      if (
        // Our consecutive series has ended because the previous character index is discontinuous with this one, skipping numbers
        previousCharIndex !== charIndex - 1 &&
        // Our consecutive series has ended because the styles have changed from the previous
        !stylesAreEqual
      ) {
        // Push the completed series to the group array and reset the series before starting fresh with the current charIndex
        styleGroups.push(series);
        series = [];
      }

      // Push this item to either the current series, or start a fresh one depending on the conditional above
      series.push(charIndex);
    });
    styleGroups.push(series);

    // Build out the chunks of content for this "block" of styled and unstyled content
    const contentChunks = [];
    // Style groups for this single line, these ONLY represent sections of the string, the index for every character won't necessarily be present here
    styleGroups.forEach((group, styleIndex) => {
      const isFirstGroup = group[0] === 0;

      // Add comment here
      const groupFinishesLine = group.at(-1) === line.length - 1;
      const isLastGroup = styleGroups.at(-1) === group;
      // Everything from the beginning of the string, to the first group member, is unstyled
      if (!isFirstGroup && !contentChunks.length) {
        contentChunks.push({
          type: 'text',
          text: line.slice(0, group[0]),
        });
      }

      // Push the styles for the current sequential group along with subsequent white spaces
      const styledTextEndIndex = group.at(-1) + 1;
      const remainingText = line.slice(styledTextEndIndex);
      const nextNonSpaceIndex = remainingText.search(/\S/);
      const nextIndex =
        nextNonSpaceIndex !== -1
          ? styledTextEndIndex + nextNonSpaceIndex
          : line.length;

      contentChunks.push({
        type: 'text',
        text: line.slice(group[0], nextIndex),
        // this group should have the same keys
        marks: Object.keys(lineStyles[group[0]]).map(k => ({
          type: styleTypeMap.fromFabric[k],
        })),
      });

      // If the group doesn't encompass the rest of the line
      if (!groupFinishesLine) {
        const unstyledTextStartIndex = isLastGroup
          ? group.at(-1) + 1
          : styleGroups[styleIndex + 1][0];
        const preceedingText = line.slice(nextIndex, unstyledTextStartIndex);

        // Add unstyled text with preceding white spaces
        if (preceedingText.trim() !== '') {
          contentChunks.push({
            type: 'text',
            text: preceedingText,
          });
        }
      }
    });

    result.content.push({
      type: 'paragraph',
      content: contentChunks,
    });
  });

  return result;
};
